import { Players, Tableau } from "../../types";
import { SpiddDraftMatch } from "../../types/SpiddDraftMatch";

export type SpiddDraftSortKey =
  | "tableau"
  | "type"
  | "phase"
  | "players"
  | "status"
  | "suggestion"
  | "alerts";

export type SpiddDraftSortDir = "asc" | "desc";

export function boardDisplayName(
  board: Pick<Tableau, "name" | "label"> | null | undefined
): string {
  if (!board) return "—";
  const name = String(board.name || "").trim();
  const label = String(board.label || "").trim();
  if (name && label) return `${name} - ${label}`;
  return name || label || "—";
}

export function playerDisplayName(
  player: Pick<Players, "surname" | "name"> | null | undefined,
  fallbackLicence?: string | null
): string {
  if (player) {
    const surname = String(player.surname || "").trim();
    const name = String(player.name || "").trim();
    const full = `${surname} ${name}`.trim();
    if (full) return full;
  }
  return fallbackLicence?.trim() || "Joueur inconnu";
}

export function draftPhaseLabel(draft: SpiddDraftMatch): string {
  return (
    draft.meta?.poolLabel ||
    draft.meta?.roundLabel ||
    draft.sourcePhaseId ||
    "—"
  );
}

export function draftTypeLabel(type: SpiddDraftMatch["type"]): string {
  if (type === "POULE_GROUP") return "Poule";
  if (type === "KNOCKOUT") return "Éliminatoire";
  return type;
}

export function draftSuggestionLabel(
  suggestion: SpiddDraftMatch["matchSuggestion"] | undefined
): string {
  if (suggestion === "CREATE") return "Créer";
  if (suggestion === "LINK") return "Associer";
  if (suggestion === "CONFLICT") return "Conflit";
  return "—";
}

export function compareDrafts(
  a: SpiddDraftMatch,
  b: SpiddDraftMatch,
  sortKey: SpiddDraftSortKey,
  sortDir: SpiddDraftSortDir,
  getTableauLabel: (draft: SpiddDraftMatch) => string,
  getPlayersLabel: (draft: SpiddDraftMatch) => string
): number {
  const dir = sortDir === "asc" ? 1 : -1;
  const value = (draft: SpiddDraftMatch): string => {
    switch (sortKey) {
      case "tableau":
        return getTableauLabel(draft);
      case "type":
        return draftTypeLabel(draft.type);
      case "phase":
        return draftPhaseLabel(draft);
      case "players":
        return getPlayersLabel(draft);
      case "status":
        return draft.status;
      case "suggestion":
        return draftSuggestionLabel(draft.matchSuggestion);
      case "alerts":
        return (draft.anomalies || []).map((x) => x.code).join(",");
      default:
        return "";
    }
  };

  const primary = value(a).localeCompare(value(b), "fr", {
    sensitivity: "base",
    numeric: true,
  });
  if (primary !== 0) return primary * dir;

  const tableauCmp = getTableauLabel(a).localeCompare(getTableauLabel(b), "fr", {
    sensitivity: "base",
    numeric: true,
  });
  if (tableauCmp !== 0) return tableauCmp;

  return draftPhaseLabel(a).localeCompare(draftPhaseLabel(b), "fr", {
    sensitivity: "base",
    numeric: true,
  });
}
